// src/pages/RatingCard.js
// Link a chess.com or lichess.org account. From the day it's linked, the
// student's rating is sampled once per day (whenever they open the app) and
// stored in chessAccounts/{uid}. A graph shows the journey from link-day to
// today. Both APIs are public and CORS-friendly; no keys needed.
import React, { useState, useEffect, useCallback } from "react";
import { db } from "../firebase";
import { doc, getDoc, setDoc, deleteDoc } from "firebase/firestore";
import { useAuth } from "../AuthContext";
import "./RatingCard.css";

const CATEGORIES_CHESSCOM = ["chess_rapid", "chess_blitz", "chess_bullet", "chess_daily"];
const CATEGORIES_LICHESS = ["rapid", "blitz", "bullet", "classical", "correspondence"];

function todayStr() {
  return new Date().toISOString().slice(0, 10);   // YYYY-MM-DD
}

// Fetch { rating, category, games, wins } for a username on a platform.
// If preferCategory is set (already-linked account), only that category is read
// so the graph always tracks the same rating type.
async function fetchRating(platform, username, preferCategory) {
  if (platform === "chess.com") {
    const res = await fetch(`https://api.chess.com/pub/player/${encodeURIComponent(username.toLowerCase())}/stats`);
    if (!res.ok) throw new Error(res.status === 404 ? "Username not found on chess.com" : "chess.com is not responding");
    const data = await res.json();
    const cats = preferCategory ? [preferCategory] : CATEGORIES_CHESSCOM;
    for (const c of cats) {
      const r = data?.[c]?.last?.rating;
      if (typeof r === "number") {
        const rec = data[c]?.record || {};
        const wins = rec.win || 0;
        const games = (rec.win || 0) + (rec.loss || 0) + (rec.draw || 0);
        return { rating: r, category: c, games, wins };
      }
    }
    throw new Error("No rated games found for this account yet");
  } else {
    const res = await fetch(`https://lichess.org/api/user/${encodeURIComponent(username)}`);
    if (!res.ok) throw new Error(res.status === 404 ? "Username not found on lichess" : "lichess is not responding");
    const data = await res.json();
    const cats = preferCategory ? [preferCategory] : CATEGORIES_LICHESS;
    for (const c of cats) {
      const p = data?.perfs?.[c];
      if (p && typeof p.rating === "number" && !p.prov) {
        return { rating: p.rating, category: c, games: p.games || 0, wins: null };
      }
    }
    // fall back to a provisional rating rather than failing outright
    for (const c of cats) {
      const p = data?.perfs?.[c];
      if (p && typeof p.rating === "number") return { rating: p.rating, category: c, games: p.games || 0, wins: null };
    }
    throw new Error("No rated games found for this account yet");
  }
}

function prettyCategory(cat) {
  return (cat || "").replace("chess_", "");
}

// Hand-rolled SVG line graph — no library needed.
function RatingGraph({ history }) {
  const W = 560, H = 220, PAD = 34;
  if (!history || history.length === 0) return null;
  const ratings = history.map(h => h.r);
  let min = Math.min(...ratings), max = Math.max(...ratings);
  if (max - min < 40) { const mid = (max + min) / 2; min = mid - 20; max = mid + 20; }  // avoid a flat-looking line
  const x = i => history.length === 1 ? W / 2 : PAD + (i * (W - PAD * 2)) / (history.length - 1);
  const y = r => H - PAD - ((r - min) * (H - PAD * 2)) / (max - min);
  const pts = history.map((h, i) => `${x(i)},${y(h.r)}`).join(" ");
  const up = history[history.length - 1].r >= history[0].r;
  const line = up ? "#2ED573" : "#FF4757";
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="rc-graph" role="img" aria-label="Rating history graph">
      {/* subtle grid */}
      {[0.25, 0.5, 0.75].map(f => (
        <line key={f} x1={PAD} x2={W - PAD} y1={PAD + f * (H - PAD * 2)} y2={PAD + f * (H - PAD * 2)} stroke="#2a2f3d" strokeWidth="1" />
      ))}
      {/* min / max labels */}
      <text x={6} y={y(max) + 4} className="rc-graph-label">{max}</text>
      <text x={6} y={y(min) + 4} className="rc-graph-label">{min}</text>
      {history.length > 1 && (
        <polyline points={pts} fill="none" stroke={line} strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
      )}
      {history.map((h, i) => (
        <circle key={h.d} cx={x(i)} cy={y(h.r)} r={history.length > 20 ? 2.5 : 4.5} fill={line} stroke="#0d0f14" strokeWidth="1.5" />
      ))}
      {/* first & last date labels */}
      <text x={PAD} y={H - 8} className="rc-graph-label">{history[0].d.slice(5)}</text>
      {history.length > 1 && (
        <text x={W - PAD} y={H - 8} textAnchor="end" className="rc-graph-label">{history[history.length - 1].d.slice(5)}</text>
      )}
    </svg>
  );
}

export default function RatingCard() {
  const { user } = useAuth();
  const [account, setAccount] = useState(undefined);   // undefined = loading, null = none linked
  const [platform, setPlatform] = useState("chess.com");
  const [username, setUsername] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const uid = user?.uid || null;

  // Load the linked account, and sample today's rating if not sampled yet.
  const load = useCallback(async () => {
    if (!uid) return;
    const accountRef = doc(db, "chessAccounts", uid);
    try {
      const snap = await getDoc(accountRef);
      if (!snap.exists()) { setAccount(null); return; }
      const data = snap.data();
      setAccount(data);
      const history = data.history || [];
      const today = todayStr();
      if (!history.some(h => h.d === today)) {
        try {
          const { rating, games, wins } = await fetchRating(data.platform, data.username, data.category);
          const newHistory = [...history, { d: today, r: rating }];
          const updated = { ...data, history: newHistory, games, wins };
          await setDoc(accountRef, updated, { merge: true });
          setAccount(updated);
        } catch (e) {
          // sampling failed (offline / API down) — keep showing stored history
          console.warn("Rating sample failed:", e.message);
        }
      }
    } catch (err) {
      console.error("Could not load chess account:", err);
      setAccount(null);
    }
  }, [uid]);

  useEffect(() => { load(); }, [load]);

  async function link() {
    const name = username.trim();
    if (!name) { setError("Type your username first"); return; }
    setBusy(true); setError("");
    try {
      const { rating, category, games, wins } = await fetchRating(platform, name);
      const data = {
        platform,
        username: name,
        category,
        linkedAt: todayStr(),
        history: [{ d: todayStr(), r: rating }],
        games, wins,
      };
      await setDoc(doc(db, "chessAccounts", uid), data);
      setAccount(data);
      setUsername("");
    } catch (err) {
      setError(err.message || "Could not link the account");
    }
    setBusy(false);
  }

  async function unlink() {
    if (!window.confirm("Unlink this account? Your rating history will be removed.")) return;
    try { await deleteDoc(doc(db, "chessAccounts", uid)); setAccount(null); } catch (err) { setError("Could not unlink — try again"); }
  }

  if (account === undefined) {
    return <div className="rating-card"><div className="loading-screen" style={{ minHeight: 160 }}><div className="spinner" /></div></div>;
  }

  // ── Not linked yet ──
  if (account === null) {
    return (
      <div className="rating-card">
        <h2 className="student-section-title" style={{ padding: 0 }}>📈 My Rating</h2>
        <p className="rc-hint">Link your chess.com or lichess account to track your rating journey — from today onward.</p>
        <div className="rc-link-form">
          <div className="rc-platforms">
            {["chess.com", "lichess"].map(p => (
              <button key={p} className={`rc-platform-btn ${platform === p ? "active" : ""}`} onClick={() => setPlatform(p)}>
                {p === "chess.com" ? "♟ chess.com" : "♞ lichess.org"}
              </button>
            ))}
          </div>
          <div className="rc-username-row">
            <input
              value={username}
              onChange={e => setUsername(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") link(); }}
              placeholder={`Your ${platform} username`}
            />
            <button className="btn btn-primary" onClick={link} disabled={busy}>{busy ? "Checking…" : "Link"}</button>
          </div>
          {error && <p className="rc-error">{error}</p>}
        </div>
      </div>
    );
  }

  // ── Linked: show the journey ──
  const history = account.history || [];
  const current = history.length ? history[history.length - 1].r : null;
  const first = history.length ? history[0].r : null;
  const delta = current !== null && first !== null ? current - first : 0;
  const winRate = account.games && account.wins != null ? Math.round((account.wins / account.games) * 100) : null;

  return (
    <div className="rating-card">
      <div className="rc-head">
        <h2 className="student-section-title" style={{ padding: 0, margin: 0 }}>📈 My Rating</h2>
        <button className="btn btn-secondary rc-unlink" onClick={unlink}>Unlink</button>
      </div>
      <p className="rc-account-line">
        <b>{account.username}</b> on {account.platform} · {prettyCategory(account.category)} · tracking since {account.linkedAt}
      </p>

      <div className="rc-stats">
        <div className="rc-stat">
          <span className="rc-stat-num">{current ?? "—"}</span>
          <span className="rc-stat-label">current rating</span>
        </div>
        <div className="rc-stat">
          <span className={`rc-stat-num ${delta > 0 ? "up" : delta < 0 ? "down" : ""}`}>
            {delta > 0 ? `▲ +${delta}` : delta < 0 ? `▼ ${delta}` : "—"}
          </span>
          <span className="rc-stat-label">since linking</span>
        </div>
        {account.games != null && (
          <div className="rc-stat">
            <span className="rc-stat-num">{account.games}</span>
            <span className="rc-stat-label">rated games</span>
          </div>
        )}
        {winRate !== null && (
          <div className="rc-stat">
            <span className="rc-stat-num">{winRate}%</span>
            <span className="rc-stat-label">win rate</span>
          </div>
        )}
      </div>

      {history.length === 1 && (
        <p className="rc-hint">Your journey starts today! Come back tomorrow and the graph begins. 🌱</p>
      )}
      <RatingGraph history={history} />
    </div>
  );
}
